import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, UserCog, Pencil } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useConfirm } from "@/components/ConfirmDialog";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "therapist" });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const confirmDialog = useConfirm();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      setEditing(null);
      setForm({ email: "", password: "", name: "", role: "therapist" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email || "", password: "", role: u.role || "therapist" });
    setOpen(true);
  };
  const openCreate = () => {
    setEditing(null);
    setForm({ email: "", password: "", name: "", role: "therapist" });
    setOpen(true);
  };

  const remove = async (u) => {
    const ok = await confirmDialog({
      title: "Delete user?",
      description: `Remove ${u.name} (${u.email}). Their headsets will be unassigned and patient roster will be detached.`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const roleBadge = (r) => {
    const map = {
      admin: "bg-slate-900 text-white",
      therapist: "bg-amber-100 text-amber-800 border-amber-200",
    };
    return <Badge variant="outline" className={`${map[r]} text-[10px] uppercase tracking-[0.12em] font-semibold`}>{r}</Badge>;
  };

  return (
    <div>
      <PageHeader
        eyebrow="Team"
        title="Therapists & Admins"
        description="Manage console accounts. Therapists can be assigned headsets. Admins can do everything therapists can."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button" onClick={openCreate} className="btn-brand rounded-full">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.75} /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display">{editing ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Name</Label>
                  <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Email</Label>
                  <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">{editing ? "New password (leave blank to keep)" : "Temporary password"}</Label>
                  <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="therapist">Therapist</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
                <Button data-testid="user-submit-button" onClick={submit} className="btn-brand rounded-full">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="panel rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-purple-50/50">
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={4} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            : users.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-16"><UserCog className="h-8 w-8 text-slate-300 mx-auto mb-2" /><div className="text-sm text-slate-500">No users yet</div></TableCell></TableRow>
            : users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50/60">
                <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                <TableCell className="text-sm text-slate-600 font-mono">{u.email}</TableCell>
                <TableCell>{roleBadge(u.role)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                    <Pencil className="h-4 w-4 text-slate-400 hover:text-purple-700" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(u)} data-testid={`user-delete-${u.id}`}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" strokeWidth={1.5} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
